import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableContainer,
  TableCell,
  TableRow,
  TablePagination,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { commonRunQuery } from "../../../util/API/loans";
import { userScoreAdd, userScoreEdit } from "../../../util/API/loans";
import { setAsArray } from "../../../util/utilfunctions/utilFunctions";
import CustomAlert from "../../UI/CustomAlert/CustomAlert";

function ScoreHistory({ selectedUser }) {
  const [currentScores, setCurrentScores] = useState([]);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [filterSources, setFilterSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedFilterSource, setSelectedFilterSource] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [scoreValue, setScoreValue] = useState("");
  const [metadata, setMetadata] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);

  const [alert, setAlert] = useState({
    showAlert: false,
    severity: "success",
    header: "",
    message: "",
  });

  const isAdmin = sessionStorage.getItem("isAdmin") === "true";
  const location = "YOUR_LOCATION";

  useEffect(() => {
    if (selectedUser) fetchAllData();
  }, [selectedUser]);

  const fetchAllData = async () => {
    setIsLoading(true);
    await Promise.all([
      fetchCurrentScores(),
      fetchScoreHistory(),
      fetchSourceOptions(),
      fetchFilterSources(),
    ]);
    setIsLoading(false);
  };

  const fetchCurrentScores = async () => {
    try {
      const response = await commonRunQuery(338, selectedUser);
      if (response?.data?.header?.callStatus === "1") {
        const rawData = setAsArray(response?.data?.body?.record);
        const formatted = rawData.map((item) => ({
          sourceid: item.userscores?.scoresourceid || "",
          sourcename: item.userscoresources?.sourcename || "Unknown Source",
          scoreValue: item.userscores?.score || "-",
          scoreDateTime: item.userscores?.updated || "",
        }));
        setCurrentScores(formatted);
      } else {
        setCurrentScores([]);
      }
    } catch (error) {
      console.error(error);
      setCurrentScores([]);
    }
  };

  const fetchScoreHistory = async () => {
    try {
      const response = await commonRunQuery(335, selectedUser);
      if (response?.data?.header?.callStatus === "1") {
        const rawData = setAsArray(response?.data?.body?.record);
        const formatted = rawData.map((item) => ({
          recid: item.ush.recid,
          sourceid: item.ush.sourceid,
          sourcename: item.uss.sourcename,
          scoreValue: item.ush.scorevalue,
          scoreDateTime: item.ush.scoredaetime,
        }));
        setScoreHistory(formatted);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSourceOptions = async () => {
    try {
      const response = await commonRunQuery(339, "");
      if (response?.data?.header?.callStatus === "1") {
        const rawData = setAsArray(response?.data?.body?.record);
        const formatted = rawData.map((item) => ({
          sourceid: item.userscoresources.sourceid,
          sourcename: item.userscoresources.sourcename,
        }));
        setSourceOptions(formatted);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFilterSources = async () => {
    try {
      const response = await commonRunQuery(336, "");
      if (response?.data?.header?.callStatus === "1") {
        const rawData = setAsArray(response?.data?.body?.record);
        const formatted = rawData.map((item) => ({
          sourceid: item.userscoresources.sourceid,
          sourcename: item.userscoresources.sourcename,
        }));
        setFilterSources(formatted);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const [d, t] = date.split(" ");
    return `${d.slice(2, 10)} ${t || ""}`;
  };

  const handleOpenAddModal = () => {
    setIsEditMode(false);
    setSelectedSource(null);
    setScoreValue("");
    setMetadata("");
    setOpenModal(true);
  };

  const handleOpenEditModal = (score) => {
    setIsEditMode(true);
    setSelectedSource({ sourceid: score.sourceid, sourcename: score.sourcename });
    setScoreValue(score.scoreValue);
    setMetadata("");
    setOpenModal(true);
  };

  const handleSubmitScore = async () => {
    if (!selectedSource || !scoreValue) {
      setAlert({ showAlert: true, severity: "error", header: "Error", message: "Source and score required." });
      return;
    }

    setLoadingAction(true);
    try {
      let metaValue = metadata.trim() || "{}";
      try { JSON.parse(metaValue); } catch {
        setAlert({ showAlert: true, severity: "warning", header: "Invalid JSON", message: "Using {} instead." });
        metaValue = "{}";
      }

      const payload = {
        location,
        userid: selectedUser,
        scoresourceid: selectedSource.sourceid,
        score: scoreValue,
        meta: metaValue,
      };

      const response = isEditMode ? await userScoreEdit(payload) : await userScoreAdd(payload);

      if (response?.callStatus === "1") {
        setAlert({ showAlert: true, severity: "success", header: "Success", message: isEditMode ? "Score updated!" : "Score added!" });
        setOpenModal(false);
        fetchAllData();
      } else {
        setAlert({ showAlert: true, severity: "error", header: "Error", message: response?.msg || "Failed." });
      }
    } catch (error) {
      setAlert({ showAlert: true, severity: "error", header: "Error", message: "Network error." });
    } finally {
      setLoadingAction(false);
    }
  };

  const filteredHistory = selectedFilterSource
    ? scoreHistory.filter((i) => i.sourceid === selectedFilterSource.sourceid)
    : scoreHistory;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: "medium" }}>
        User Score Management
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ bgcolor: "#fff", border: "1px solid #e0e0e0", borderRadius: 1 }}>
          {/* HEADER WITH ADD BUTTON */}
          <Box sx={{ px: 2, py: 1.5, display: "flex", bgcolor: "#fff",justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
              Scores
            </Typography>
            {isAdmin && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleOpenAddModal}
                sx={{ bgcolor: "#4caf50", "&:hover": { bgcolor: "#45a049" }, textTransform: "none" }}
              >
                Add Score
              </Button>
            )}
          </Box>

          {/* CURRENT SCORES */}
          <Box sx={{ px: 2, py: 2,bgcolor: "#fff"}}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: "medium" }}>
              Current Scores ({currentScores.length})
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 1 }}>Source</TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>Score</TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>Updated</TableCell>
                    <TableCell align="center" sx={{ py: 1 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentScores.length > 0 ? (
                    currentScores.map((s) => (
                      <TableRow key={s.sourceid}>
                        <TableCell sx={{ py: 1.5 }}>{s.sourcename}</TableCell>
                        <TableCell align="center" sx={{ py: 1.5, fontWeight: "medium" }}>
                          {s.scoreValue}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5, fontSize: 14, color: "text.secondary" }}>
                          {formatDate(s.scoreDateTime)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          {isAdmin && (
                            <IconButton size="small" onClick={() => handleOpenEditModal(s)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No current scores available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* NICE GAP + DIVIDER */}
          <Box sx={{ mx: 2 ,bgcolor: "#fff"}}>
            <Divider sx={{ my: 3,bgcolor: "#fff" }} />
          </Box>

          {/* SCORE HISTORY */}
          <Box sx={{ px: 2, pb: 2,bgcolor: "#fff" }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: "medium" }}>
              Score History
            </Typography>

            <Box sx={{ maxWidth: 300, mb: 2 }}>
              <Autocomplete
                size="small"
                options={filterSources}
                getOptionLabel={(o) => o.sourcename}
                value={selectedFilterSource}
                onChange={(e, v) => {
                  setSelectedFilterSource(v);
                  setHistoryPage(0);
                }}
                renderInput={(p) => <TextField {...p} label="Filter by Source" variant="outlined" />}
                clearOnEscape
              />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 1 }}>Date & Time</TableCell>
                    <TableCell sx={{ py: 1 }}>Source</TableCell>
                    <TableCell sx={{ py: 1 }}>Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistory.length > 0 ? (
                    filteredHistory
                      .slice(historyPage * rowsPerPage, historyPage * rowsPerPage + rowsPerPage)
                      .map((r) => (
                        <TableRow key={r.recid}>
                          <TableCell sx={{ py: 1.5, fontSize: 14 }}>{formatDate(r.scoreDateTime)}</TableCell>
                          <TableCell sx={{ py: 1.5 }}>{r.sourcename}</TableCell>
                          <TableCell sx={{ py: 1.5 }}>{r.scoreValue}</TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No history available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredHistory.length}
              rowsPerPage={rowsPerPage}
              page={historyPage}
              onPageChange={(e, p) => setHistoryPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setHistoryPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
        </Paper>
      )}

      {/* MODAL */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? "Edit Score" : "Add New Score"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <Autocomplete
              options={sourceOptions}
              getOptionLabel={(o) => o.sourcename}
              value={selectedSource}
              onChange={(e, v) => setSelectedSource(v)}
              renderInput={(p) => <TextField {...p} label="Source *" required margin="dense" fullWidth />}
              disabled={isEditMode}
            />
            <TextField
              label="Score *"
              type="number"
              fullWidth
              margin="dense"
              value={scoreValue}
              onChange={(e) => setScoreValue(e.target.value)}
            />
            <TextField
              label="Metadata (JSON, optional)"
              fullWidth
              margin="dense"
              multiline
              rows={3}
              value={metadata}
              onChange={(e) => setMetadata(e.target.value)}
              placeholder="Leave blank → {}"
              helperText="Invalid JSON → {}"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmitScore}
            disabled={loadingAction || !selectedSource || !scoreValue}
          >
            {loadingAction ? <CircularProgress size={20} /> : isEditMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomAlert
        severity={alert.severity}
        show={alert.showAlert}
        header={alert.header}
        message={alert.message}
        onClick={() => setAlert({ ...alert, showAlert: false })}
        autoclose={alert.severity === "success"}
      />
    </Box>
  );
}

export default ScoreHistory;